<template>
  <b-modal
    v-model="modalShowing"
    :title="isUpdate ? $t('companySiteModal.editTitle') : $t('companySiteModal.addTitle')"
    size="lg"
    centered
    content-class="bg-gray-100"
    hide-footer
    @hidden="onModalHidden"
  >
    <b-overlay :show="loading || saving">
      <formulate-form
        v-if="formReady"
        v-model="formData"
        ref="siteForm"
        @submit="onSubmit"
      >
        <formulate-input
          name="name"
          :label="$t('companySiteModal.siteName')"
          :help="$t('companySiteModal.siteNameHelp')"
          :outer-class="['mb-3']"
        ></formulate-input>

        <formulate-input
          name="site_type_ids"
          type="checkbox"
          :label="$t('companySiteModal.siteTypes')"
          :options="siteTypeOptions"
          :outer-class="['mb-3']"
        ></formulate-input>

        <hr />

        <div class="d-flex w-100">
          <formulate-input
            name="addr1"
            :label="$t('companySiteModal.streetAddress')"
            :outer-class="['flex-fill', 'w-65', 'pr-2', 'mb-2']"
          ></formulate-input>
          <formulate-input
            name="subpremise"
            :label="$t('companySiteModal.subpremise')"
            :outer-class="['flex-fill', 'w-35', 'mb-2']"
          ></formulate-input>
        </div>

        <formulate-input
          name="addr2"
          :label="$t('companySiteModal.streetAddress2')"
          :outer-class="['mb-2']"
        ></formulate-input>

        <formulate-input
          name="addr3"
          :label="$t('companySiteModal.streetAddress3')"
          :outer-class="['mb-2']"
        ></formulate-input>

        <div class="d-flex w-100">
          <formulate-input
            name="city"
            :label="$t('companySiteModal.city')"
            :outer-class="['flex-fill', 'w-40', 'pr-2', 'mb-2']"
          ></formulate-input>
          <formulate-input
            name="state"
            :label="$t('companySiteModal.state')"
            :outer-class="['flex-fill', 'w-30', 'pr-2', 'mb-2']"
          ></formulate-input>
          <formulate-input
            name="postcode"
            :label="$t('companySiteModal.postcode')"
            :outer-class="['flex-fill', 'w-30', 'mb-2']"
          ></formulate-input>
        </div>

        <formulate-input
          name="country"
          type="select"
          :label="$t('companySiteModal.country')"
          :options="countryOptions"
          :placeholder="$t('companySiteModal.selectCountry')"
          :outer-class="['max-width-250 d-inline-block mr-3']"
        ></formulate-input>
        <country-flag
          v-if="formData.country"
          :country="formData.country"
        ></country-flag>

        <hr />

        <formulate-input
          name="is_primary"
          type="checkbox"
          :label="$t('companySiteModal.primarySite')"
          :help="isCurrentlyPrimary ? $t('companySiteModal.primaryHelp') : ''"
          :disabled="isCurrentlyPrimary"
          :outer-class="['mb-3']"
        ></formulate-input>

        <formulate-input
          name="notes"
          type="textarea"
          :label="$t('companySiteModal.notes')"
          :outer-class="['mb-3']"
        ></formulate-input>

        <div class="d-flex align-items-center">
          <formulate-input
            type="submit"
            :label="$t('buttons.save')"
            :disabled="saving"
            :outer-class="['mb-0', 'mr-auto']"
          ></formulate-input>
          <b-button
            v-if="isUpdate"
            variant="outline-danger"
            size="sm"
            :disabled="saving"
            @click="onDelete"
          >
            <b-icon
              icon="trash"
              class="mr-1"
            ></b-icon>
            {{ $t('companySiteModal.deleteSite') }}
          </b-button>
        </div>
      </formulate-form>
    </b-overlay>
  </b-modal>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import { errorHandling } from '@/js/custom-vue-mixins';

  // create / edit / delete one CompanySite -- ONLY for tenants with custom_flags.use_vendor_sites
  // emits 'saved' (with the site data) and 'deleted' (with the site id)
  const ADDRESS_FIELDS = ['addr1', 'addr2', 'addr3', 'subpremise', 'city', 'state', 'postcode', 'country']; // raw_address is not edited here

  export default {
    name: 'CompanySiteModal',
    components: {},
    mixins: [errorHandling], // errorToaster(error, content)
    i18n: {
      messages: {
        en: {
          companySiteModal: {
            addTitle: 'Add Site',
            editTitle: 'Edit Site',
            siteName: 'Site Name',
            siteNameHelp: 'If blank, the city is used as the name',
            siteTypes: 'Site Types',
            streetAddress: 'Street Address',
            subpremise: 'Floor / Suite / Unit',
            streetAddress2: 'Street Address 2',
            streetAddress3: 'Street Address 3',
            city: 'City',
            state: 'State / Province',
            postcode: 'Postal Code',
            country: 'Country',
            selectCountry: 'Select a country...',
            primarySite: 'Primary site',
            primaryHelp: 'To change the primary site, make another site primary.',
            notes: 'Notes',
            deleteSite: 'Delete Site',
            deleteConfirm: 'Delete this site? This cannot be undone.',
            errorLoading: 'Error while loading the site',
            errorSaving: 'Error while saving the site',
            errorDeleting: 'Error while deleting the site',
          },
        },
        es: {
          companySiteModal: {
            addTitle: 'Agregar Sitio',
            editTitle: 'Editar Sitio',
            siteName: 'Nombre del Sitio',
            siteNameHelp: 'Si se deja en blanco, se usa la ciudad como nombre',
            siteTypes: 'Tipos de Sitio',
            streetAddress: 'Dirección',
            subpremise: 'Piso / Oficina / Unidad',
            streetAddress2: 'Dirección 2',
            streetAddress3: 'Dirección 3',
            city: 'Ciudad',
            state: 'Estado / Provincia',
            postcode: 'Código Postal',
            country: 'País',
            selectCountry: 'Seleccione un país...',
            primarySite: 'Sitio principal',
            primaryHelp: 'Para cambiar el sitio principal, marque otro sitio como principal.',
            notes: 'Notas',
            deleteSite: 'Eliminar Sitio',
            deleteConfirm: '¿Eliminar este sitio? Esta acción no se puede deshacer.',
            errorLoading: 'Error al cargar el sitio',
            errorSaving: 'Error al guardar el sitio',
            errorDeleting: 'Error al eliminar el sitio',
          },
        },
        pt: {
          companySiteModal: {
            addTitle: 'Adicionar Unidade',
            editTitle: 'Editar Unidade',
            siteName: 'Nome da Unidade',
            siteNameHelp: 'Se ficar em branco, a cidade é usada como nome',
            siteTypes: 'Tipos de Unidade',
            streetAddress: 'Endereço',
            subpremise: 'Andar / Sala / Unidade',
            streetAddress2: 'Endereço 2',
            streetAddress3: 'Endereço 3',
            city: 'Cidade',
            state: 'Estado / Província',
            postcode: 'CEP',
            country: 'País',
            selectCountry: 'Selecione um país...',
            primarySite: 'Unidade principal',
            primaryHelp: 'Para alterar a unidade principal, marque outra unidade como principal.',
            notes: 'Observações',
            deleteSite: 'Excluir Unidade',
            deleteConfirm: 'Excluir esta unidade? Esta ação não pode ser desfeita.',
            errorLoading: 'Erro ao carregar a unidade',
            errorSaving: 'Erro ao salvar a unidade',
            errorDeleting: 'Erro ao excluir a unidade',
          },
        },
        'zh-Hans': {
          companySiteModal: {
            addTitle: '添加站点',
            editTitle: '编辑站点',
            siteName: '站点名称',
            siteNameHelp: '如果留空，将使用城市作为名称',
            siteTypes: '站点类型',
            streetAddress: '街道地址',
            subpremise: '楼层 / 套房 / 单元',
            streetAddress2: '街道地址 2',
            streetAddress3: '街道地址 3',
            city: '城市',
            state: '州 / 省',
            postcode: '邮政编码',
            country: '国家',
            selectCountry: '选择国家...',
            primarySite: '主要站点',
            primaryHelp: '如需更改主要站点，请将另一个站点设为主要站点。',
            notes: '备注',
            deleteSite: '删除站点',
            deleteConfirm: '删除此站点？此操作无法撤消。',
            errorLoading: '加载站点时出错',
            errorSaving: '保存站点时出错',
            errorDeleting: '删除站点时出错',
          },
        },
        'zh-Hant': {
          companySiteModal: {
            addTitle: '新增據點',
            editTitle: '編輯據點',
            siteName: '據點名稱',
            siteNameHelp: '若留空，將使用城市作為名稱',
            siteTypes: '據點類型',
            streetAddress: '街道地址',
            subpremise: '樓層 / 套房 / 單位',
            streetAddress2: '街道地址 2',
            streetAddress3: '街道地址 3',
            city: '城市',
            state: '州 / 省',
            postcode: '郵遞區號',
            country: '國家',
            selectCountry: '選擇國家...',
            primarySite: '主要據點',
            primaryHelp: '如需變更主要據點，請將另一個據點設為主要據點。',
            notes: '備註',
            deleteSite: '刪除據點',
            deleteConfirm: '刪除此據點？此動作無法復原。',
            errorLoading: '載入據點時發生錯誤',
            errorSaving: '儲存據點時發生錯誤',
            errorDeleting: '刪除據點時發生錯誤',
          },
        },
      },
    },
    props: {
      companyId: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        modalShowing: false,
        loading: false,
        saving: false,
        formReady: false,
        siteId: null, // set when editing an existing site
        isCurrentlyPrimary: false,
        currentSiteTypeIds: [], // types already on the site -- kept as options even if retired (allow_new_use=false)
        siteTypes: [],
        countries: [],
        formData: {},
      };
    },
    computed: {
      isUpdate() {
        return !!this.siteId;
      },
      siteTypeOptions() {
        // retired types (allow_new_use=false) are only offered if the site already has them
        return this.siteTypes
          .filter((siteType) => siteType.allow_new_use || this.currentSiteTypeIds.includes(siteType.id))
          .map((siteType) => ({ value: siteType.id, label: siteType.name }));
      },
      countryOptions() {
        return this.countries.map((country) => ({ value: country.code, label: country.name }));
      },
    },
    methods: {
      async startCreate() {
        this.siteId = null;
        this.isCurrentlyPrimary = false;
        this.currentSiteTypeIds = [];
        this.formData = { site_type_ids: [], is_primary: false };
        await this.open();
      },
      async startUpdate(siteId) {
        this.siteId = siteId;
        await this.open();
      },
      async open() {
        this.formReady = false;
        this.modalShowing = true;
        this.loading = true;
        try {
          const [siteTypesResponse, formDataResponse, siteResponse] = await Promise.all([
            suppliosApi.companySiteType.list(),
            suppliosApi.locations.loadAddressFormData(),
            this.siteId ? suppliosApi.companySite.retrieve(this.siteId) : Promise.resolve(null),
          ]);
          this.siteTypes = siteTypesResponse.data;
          this.countries = formDataResponse.data.countries || [];
          if (siteResponse) {
            this.setFormFromSite(siteResponse.data);
          }
          this.formReady = true;
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteModal.errorLoading'));
          this.modalShowing = false;
        } finally {
          this.loading = false;
        }
      },
      setFormFromSite(site) {
        const address = site.address || {};
        this.isCurrentlyPrimary = site.is_primary;
        this.currentSiteTypeIds = site.site_types.map((siteType) => siteType.id);
        const formData = {
          name: site.name,
          notes: site.notes,
          is_primary: site.is_primary,
          site_type_ids: [...this.currentSiteTypeIds],
        };
        ADDRESS_FIELDS.forEach((field) => {
          formData[field] = address[field] || '';
        });
        this.formData = formData;
      },
      buildPayload(values) {
        const address = {};
        ADDRESS_FIELDS.forEach((field) => {
          address[field] = values[field] || '';
        });
        return {
          company: this.companyId,
          name: values.name || '',
          notes: values.notes || '',
          is_primary: !!values.is_primary,
          site_type_ids: values.site_type_ids || [],
          address,
        };
      },
      async onSubmit(values) {
        this.saving = true;
        try {
          const payload = this.buildPayload(values);
          const response = this.isUpdate ? await suppliosApi.companySite.partialUpdate(this.siteId, payload) : await suppliosApi.companySite.create(payload);
          this.$emit('saved', response.data);
          this.modalShowing = false;
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteModal.errorSaving'));
        } finally {
          this.saving = false;
        }
      },
      async onDelete() {
        const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('companySiteModal.deleteConfirm'), {
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('companySiteModal.deleteSite'),
          cancelTitle: this.$t('buttons.cancel'),
          centered: true,
        });
        if (!confirmed) return;
        this.saving = true;
        try {
          const deletedId = this.siteId;
          await suppliosApi.companySite.destroy(deletedId);
          this.$emit('deleted', deletedId);
          this.modalShowing = false;
        } catch (error) {
          this.errorToaster(error, this.$t('companySiteModal.errorDeleting'));
        } finally {
          this.saving = false;
        }
      },
      onModalHidden() {
        this.formReady = false;
        this.siteId = null;
      },
    },
  };
</script>
